import { useState, useEffect, useCallback, useRef } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';

export interface DraftChatMessage {
  id: string;
  draft_id: string;
  user_id: string;
  team_name: string;
  message: string;
  created_at: string;
}

/**
 * SAB-848: which table a chat lives in, and how it is keyed.
 *
 * MLB and NFL store draft chat in structurally different places and neither can host the
 * other's rows: `draft_chat.draft_id` FKs to `drafts`, which NFL has no equivalent of --
 * its draft state lives in `nfl_leagues.settings`. Rather than fork this hook or the
 * component that owns it, the source is a parameter with MLB's shape as the default, so
 * every existing caller is untouched.
 */
export interface DraftChatSource {
  /** Table holding the messages. */
  table: string;
  /** Column the messages are scoped by -- 'draft_id' for MLB, 'league_id' for NFL. */
  keyColumn: string;
  /** Realtime channel name prefix; the scope id is appended. */
  channelPrefix: string;
  /**
   * Optional discriminator. NFL reuses `nfl_chat_messages`, which also carries
   * commissioner broadcasts, so draft chat filters to message_type='draft' on both the
   * initial read AND the realtime filter -- otherwise a broadcast would appear live in
   * the draft room while being absent after a reload.
   */
  typeColumn?: string;
  typeValue?: string;
  /**
   * Whether the table denormalises the author's team name onto each row.
   *
   * MLB's draft_chat has a team_name column. nfl_chat_messages does not, and deliberately
   * so -- NFL resolves the display name from nfl_teams at render time, so renaming a team
   * does not leave a trail of stale names through the transcript. Declared explicitly
   * rather than inferred from the table name, which would be magic.
   */
  hasTeamNameColumn?: boolean;
}

/** MLB's dedicated draft_chat table. The default, so existing callers change nothing. */
export const MLB_DRAFT_CHAT: DraftChatSource = {
  table: 'draft_chat',
  keyColumn: 'draft_id',
  channelPrefix: 'draft-chat',
  hasTeamNameColumn: true,
};

/** NFL reuses the league-scoped chat table, discriminated by message_type. */
export const NFL_DRAFT_CHAT: DraftChatSource = {
  table: 'nfl_chat_messages',
  keyColumn: 'league_id',
  channelPrefix: 'nfl-draft-chat',
  typeColumn: 'message_type',
  typeValue: 'draft',
};

export function useDraftChat(
  scopeId: string | null,
  teamName: string | null,
  source: DraftChatSource = MLB_DRAFT_CHAT,
) {
  const { user } = useAuth();
  const [messages, setMessages] = useState<DraftChatMessage[]>([]);
  const [sending, setSending] = useState(false);
  /** Increments on every new message arrival so the component can react */
  const [newMessageFlag, setNewMessageFlag] = useState(0);

  // Destructured so the effect depends on the FIELDS, not on the object identity. A caller
  // passing an inline literal would otherwise resubscribe on every render.
  const { table, keyColumn, channelPrefix, typeColumn, typeValue, hasTeamNameColumn } = source;

  // Fetch existing messages + subscribe to new ones
  useEffect(() => {
    if (!scopeId) return;

    const fetchMessages = async () => {
      let q = supabase
        .from(table as any)
        .select('*')
        .eq(keyColumn, scopeId);
      if (typeColumn && typeValue) q = q.eq(typeColumn, typeValue);
      const { data } = await q.order('created_at', { ascending: true }).limit(200);
      if (data) {
        setMessages(data as any as DraftChatMessage[]);
        // Signal initial load so component scrolls to bottom
        setNewMessageFlag(f => f + 1);
      }
    };
    fetchMessages();

    const channel = supabase
      .channel(`${channelPrefix}-${scopeId}`)
      .on('postgres_changes', {
        // SAB-846: INSERT explicitly, never '*'. Supabase does not apply RLS to DELETE
        // events and they cannot be filtered, so a '*' subscription would hand every
        // subscriber every deletion on the table regardless of league.
        event: 'INSERT',
        schema: 'public',
        table,
        filter: `${keyColumn}=eq.${scopeId}`,
      }, (payload) => {
        // Read through an index signature rather than `any`: the discriminator column is
        // only known at runtime, and this keeps the no-explicit-any count at baseline.
        const raw = payload.new as unknown as Record<string, unknown>;
        // The realtime `filter` supports one condition, so the discriminator is applied
        // here. Without it a commissioner broadcast would appear live in the draft room
        // and then vanish on reload, since the initial read filters it out.
        if (typeColumn && typeValue && raw[typeColumn] !== typeValue) return;
        setMessages(prev => [...prev, raw as unknown as DraftChatMessage]);
        setNewMessageFlag(f => f + 1);
      })
      .subscribe();

    return () => { supabase.removeChannel(channel); };
  }, [scopeId, table, keyColumn, channelPrefix, typeColumn, typeValue]);

  const sendMessage = useCallback(async (text: string) => {
    if (!scopeId || !user?.id || !teamName || !text.trim() || sending) return;
    setSending(true);
    try {
      const row: Record<string, unknown> = {
        [keyColumn]: scopeId,
        user_id: user.id,
        message: text.trim().slice(0, 200),
      };
      if (hasTeamNameColumn) row.team_name = teamName;
      if (typeColumn && typeValue) row[typeColumn] = typeValue;
      await supabase.from(table as any).insert(row as any);
    } finally {
      setSending(false);
    }
  }, [scopeId, user?.id, teamName, sending, table, keyColumn, typeColumn, typeValue, hasTeamNameColumn]);

  return { messages, sendMessage, sending, newMessageFlag };
}
